<template>
  <v-dialog
    :value="value"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    persistent
    scrollable
    max-width="1100"
    @input="v => !v && close()"
  >
    <v-card class="import">
      <div class="fdlg__form">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">Import din Excel</h2>
            <p class="fdlg__sub">Mai multe echipamente deodată, după șablon.</p>
          </div>
          <v-btn icon aria-label="Închide" :disabled="busy" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <template v-if="!result">
            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-numeric-1-circle-outline</v-icon>Șablonul</div>
              <p class="form-section__note">
                Descărcați șablonul: are listele derulante cu tipurile, camerele, departamentele și persoanele (login).
                Un rând = un echipament.
              </p>
              <v-btn outlined color="#2d8659" class="mb-4" :loading="templateLoading" data-action="template" @click="downloadTemplate">
                <v-icon left>mdi-file-download-outline</v-icon>Descarcă șablonul
              </v-btn>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-numeric-2-circle-outline</v-icon>Fișierul completat</div>
              <v-file-input
                v-model="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                label="Alegeți fișierul .xlsx"
                prepend-icon=""
                prepend-inner-icon="mdi-microsoft-excel"
                outlined
                dense
                show-size
                :disabled="busy"
                :error-messages="fileError"
                data-field="file"
                @change="onFile"
              />
            </section>

            <section v-if="preview" class="form-section">
              <div class="form-section__label">
                <v-icon small>mdi-numeric-3-circle-outline</v-icon>Verificarea
                <span class="import__chips">
                  <v-chip small color="success" text-color="white" data-count="valid">{{ amount(preview.valid, "valid", "valide") }}</v-chip>
                  <v-chip v-if="preview.duplicates" small color="warning" text-color="white" data-count="duplicates">{{ amount(preview.duplicates, "pare deja importat", "par deja importate") }}</v-chip>
                  <v-chip v-if="preview.invalid" small color="error" text-color="white" data-count="invalid">{{ amount(preview.invalid, "cu erori", "cu erori") }}</v-chip>
                </span>
              </div>
              <v-alert v-if="preview.alreadyImported" dense text type="error" class="mb-3" data-alert="already-imported">
                {{ alreadyImportedText }}
              </v-alert>
              <p class="form-section__note">
                Se importă doar rândurile verzi. Dacă importați acum doar o parte, ștergeți din fișier rândurile importate
                înainte de a-l încărca din nou cu rândurile corectate; altfel ele ar apărea de două ori în Echipamente.
              </p>
              <v-switch
                v-if="preview.duplicates"
                v-model="includeDuplicates"
                inset
                dense
                color="#b26a00"
                class="mt-0"
                data-field="include-duplicates"
                :label="`Importă și ${amount(preview.duplicates, 'rândul care pare deja importat', 'rândurile care par deja importate')}`"
              />
              <v-switch
                v-if="preview.invalid"
                v-model="onlyErrors"
                inset
                dense
                color="#2d8659"
                class="mt-0"
                label="Doar rândurile cu erori"
              />
              <div class="import__table-wrap">
                <table class="import__table" data-table="preview">
                  <thead>
                    <tr>
                      <th>Rând</th>
                      <th>Tip</th>
                      <th>Model</th>
                      <th>Seria</th>
                      <th>Nr. inv.</th>
                      <th>Responsabil</th>
                      <th>Departament</th>
                      <th>Cameră</th>
                      <th>Rezultat</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in shownRows" :key="r.row" :class="rowClass(r)" :data-row="r.row">
                      <td class="import__num">{{ r.row }}</td>
                      <td>{{ r.type }}</td>
                      <td>{{ r.model }}</td>
                      <td>{{ r.serial }}</td>
                      <td>{{ r.registration }}</td>
                      <td>{{ r.owner }}</td>
                      <td>{{ r.department }}</td>
                      <td>{{ r.room }}</td>
                      <td class="import__result">
                        <span v-if="r.ok && !r.duplicateOf" class="import__ok"><v-icon small color="#2d8659">mdi-check-circle</v-icon>Valid</span>
                        <span v-else-if="r.ok" class="import__dup"><v-icon small color="#b26a00">mdi-content-duplicate</v-icon>{{ includeDuplicates ? "Se importă" : "Nu se importă" }}</span>
                        <ul v-if="!r.ok" class="import__errors">
                          <li v-for="(e, i) in r.errors" :key="i">{{ e }}</li>
                        </ul>
                        <ul v-if="r.warnings && r.warnings.length" class="import__warnings">
                          <li v-for="(w, i) in r.warnings" :key="'w' + i">{{ w }}</li>
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </template>

          <section v-else class="form-section import__done" data-state="done">
            <div class="form-section__label"><v-icon small>mdi-check-decagram-outline</v-icon>Import finalizat</div>
            <p class="import__done-text">
              Au fost adăugate <strong>{{ importedText }}</strong><span v-if="skipped">;
              <strong>{{ skippedText }}</strong> nu au fost importate</span>.
              Fiecare are acum codul său QR.
            </p>
            <v-btn class="btn-green mb-4" large data-action="print-imported" @click="printOpen = true">
              <v-icon left>mdi-printer-outline</v-icon>Imprimă etichetele lor
            </v-btn>
          </section>

          <v-alert v-if="error" dense text type="error" class="mb-4">{{ error }}</v-alert>
        </v-card-text>

        <div class="fdlg__foot">
          <span v-if="preview && !result" class="import__summary">
            {{ amount(preview.total, "rând", "rânduri") }} în fișier
          </span>
          <v-spacer />
          <template v-if="!result">
            <v-btn text large :disabled="busy" @click="close">Anulare</v-btn>
            <v-btn
              class="btn-green"
              large
              :disabled="!preview || !toImport || !!preview.alreadyImported || busy"
              :loading="committing"
              data-action="commit"
              @click="commit"
            >
              <v-icon left>mdi-database-import-outline</v-icon>{{ commitLabel }}
            </v-btn>
          </template>
          <v-btn v-else class="btn-green" large @click="close">Închide</v-btn>
        </div>
      </div>
    </v-card>

    <StickerPrintDialog v-model="printOpen" :items="importedItems" />
  </v-dialog>
</template>

<script>
// Echipamente → Import Excel: template, upload, per-row check (nothing saved), then the valid rows in one go
import * as bulk from "./bulkApi";
import * as helper from "@/helper/helper";
import StickerPrintDialog from "@/components/stickers/StickerPrintDialog.vue";
import { amount } from "@/helper/plural";

export default {
  name: "ImportEquipmentDialog",
  components: { StickerPrintDialog },
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      file: null,
      fileError: "",
      preview: null,
      result: null,
      error: "",
      checking: false,
      committing: false,
      templateLoading: false,
      onlyErrors: false,
      includeDuplicates: false,
      printOpen: false,
    };
  },
  computed: {
    busy() {
      return this.checking || this.committing;
    },
    shownRows() {
      const rows = (this.preview && this.preview.rows) || [];
      return this.onlyErrors ? rows.filter((r) => !r.ok) : rows;
    },
    // rows the commit would insert
    toImport() {
      if (!this.preview) return 0;
      return this.preview.valid + (this.includeDuplicates ? this.preview.duplicates || 0 : 0);
    },
    commitLabel() {
      return `Importă ${amount(this.toImport, "rând", "rânduri")}`;
    },
    alreadyImportedText() {
      const a = this.preview && this.preview.alreadyImported;
      if (!a) return "";
      const when = a.date ? helper.formatCreatedAt(a.date) : "";
      const what = amount(a.count, "echipament", "echipamente");
      return `Acest fișier a fost deja importat${when ? ` pe ${when}` : ""}${a.user ? ` de ${a.user}` : ""} (${what}). Dacă ați corectat rânduri, ștergeți din fișier rândurile importate atunci și încărcați-l din nou.`;
    },
    importedText() {
      return amount(this.result ? this.result.imported : 0, "echipament", "echipamente");
    },
    // rows with errors + rows that looked already imported and were left out
    skipped() {
      if (!this.result) return 0;
      return (this.result.total || 0) - (this.result.imported || 0);
    },
    skippedText() {
      return amount(this.skipped, "rând", "rânduri");
    },
    importedItems() {
      return ((this.result && this.result.rows) || []).filter((r) => r.ok && r.tag);
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.reset();
        helper.scrollToTop(this);
      }
    },
  },
  methods: {
    amount,
    reset() {
      this.file = null;
      this.fileError = "";
      this.preview = null;
      this.result = null;
      this.error = "";
      this.onlyErrors = false;
      this.includeDuplicates = false;
    },
    rowClass(r) {
      if (!r.ok) return "import__row--bad";
      return r.duplicateOf ? "import__row--dup" : "import__row--ok";
    },
    close() {
      if (this.busy) return;
      this.$emit("input", false);
    },
    async downloadTemplate() {
      this.templateLoading = true;
      this.error = "";
      try {
        await bulk.downloadImportTemplate();
      } catch (e) {
        this.error = await bulk.blobErrorMessage(e, "Șablonul nu a putut fi descărcat.");
      } finally {
        this.templateLoading = false;
      }
    },
    async onFile(file) {
      this.preview = null;
      this.error = "";
      this.fileError = "";
      if (!file) return;
      if (!/\.xlsx$/i.test(file.name)) {
        this.fileError = "Alegeți un fișier Excel .xlsx (completat după șablon).";
        return;
      }
      this.checking = true;
      try {
        this.preview = await bulk.previewImport(file);
        this.onlyErrors = false;
        this.includeDuplicates = false;
      } catch (e) {
        this.fileError = await bulk.blobErrorMessage(e, "Fișierul nu a putut fi verificat.");
      } finally {
        this.checking = false;
      }
    },
    async commit() {
      if (!this.file || !this.preview) return;
      this.committing = true;
      this.error = "";
      try {
        const result = await bulk.commitImport(this.file, { includeDuplicates: this.includeDuplicates });
        this.result = result;
        this.$emit("imported", result);
        helper.scrollToTop(this);
      } catch (e) {
        // an answer from the server = the whole import was refused (one transaction); no answer = it may have
        // been saved: the new check below says so ("deja importat") when it was
        this.error = e && e.response
          ? await bulk.blobErrorMessage(e, "Importul nu a reușit. Nu a fost salvat niciun rând.")
          : "Nu s-a primit răspuns de la server, deci nu se știe dacă importul s-a salvat. Verificarea de mai jos arată dacă fișierul a fost importat; nu-l importați din nou înainte de a verifica lista Echipamente.";
        // the file may have changed meanwhile (seria taken by someone else, or the commit went through): check it again
        try {
          this.preview = await bulk.previewImport(this.file);
          this.includeDuplicates = false;
        } catch (ignored) {
          // keep the old preview
        }
      } finally {
        this.committing = false;
      }
    },
  },
};
</script>

<style scoped>
.import .fdlg__form {
  max-height: 92vh;
}
.import__chips {
  display: inline-flex;
  gap: 6px;
  margin-left: auto;
}
.import__table-wrap {
  overflow-x: auto;
  margin-bottom: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.import__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.import__table th {
  position: sticky;
  top: 0;
  background: #2d8659;
  color: #fff;
  font-weight: 600;
  text-align: left;
  padding: 8px 10px;
  white-space: nowrap;
}
.import__table td {
  padding: 7px 10px;
  border-top: 1px solid #e6ede9;
  vertical-align: top;
}
.import__row--ok td {
  background: #ecf9f2;
}
.import__row--bad td {
  background: #fbe3e1;
}
.import__row--bad td:first-child {
  box-shadow: inset 4px 0 0 #b3362f;
}
.import__num {
  font-weight: 700;
  white-space: nowrap;
}
.import__result {
  min-width: 220px;
}
.import__ok {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #1f6b45;
  font-weight: 600;
}
.import__errors {
  margin: 0;
  padding-left: 16px;
  color: #b3362f;
}
.import__warnings {
  margin: 4px 0 0;
  padding-left: 16px;
  color: #8a5300;
}
.import__row--dup td {
  background: #fff4e0;
}
.import__row--dup td:first-child {
  box-shadow: inset 4px 0 0 #b26a00;
}
.import__dup {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #8a5300;
  font-weight: 600;
}
.import__summary {
  color: #5f7068;
}
.import__done-text {
  font-size: 1.05rem;
}
</style>
